import React, { useEffect, useState } from 'react';
import { Plus, GripVertical, Trash2, Save, X, Eye, EyeOff, AlertTriangle, MailCheck } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const STAGE_TYPES = [
  { v: 'info',            l: 'Info' },
  { v: 'action_required', l: 'Action required' },
  { v: 'terminal',        l: 'Terminal' },
];

const COLORS = ['#94a3b8', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#a855f7'];

function blankStage() {
  return {
    code: '', name: '', description: '', sort_order: 999,
    stage_type: 'info', icon: 'circle', color_code: '#94a3b8',
    requires_client_action: false, send_email: false, active: true,
  };
}

export default function AdminWorkflowStagesPage() {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // stage id being edited
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await api.adminListWorkflowStages();
      setStages(list || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load workflow stages');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (s) => { setEditing(s.id); setDraft({ ...s }); setCreating(false); };
  const startCreate = () => { setEditing('__new__'); setDraft(blankStage()); setCreating(true); };
  const cancel = () => { setEditing(null); setDraft(null); setCreating(false); };

  const save = async () => {
    if (!draft) return;
    try {
      if (creating) {
        if (!draft.code.trim() || !draft.name.trim()) return toast.error('Code and name are required');
        await api.adminCreateWorkflowStage(draft);
        toast.success('Stage created');
      } else {
        await api.adminUpdateWorkflowStage(editing, draft);
        toast.success('Stage updated');
      }
      cancel();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save stage'); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Deactivate "${s.name}"? Existing timelines will keep their history.`)) return;
    try {
      await api.adminDeleteWorkflowStage(s.id);
      toast.success('Stage deactivated');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not deactivate stage'); }
  };

  const moveBy = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= stages.length) return;
    const next = [...stages];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    // Renumber sort_order in steps of 10.
    const order = next.map((s, i) => ({ id: s.id, sort_order: (i + 1) * 10 }));
    setStages(next.map((s, i) => ({ ...s, sort_order: (i + 1) * 10 })));
    try { await api.adminReorderWorkflowStages(order); } catch { toast.error('Could not save new order'); }
  };

  return (
    <AdminLayout title="Workflow stages" subtitle="Configure your tax-filing progress tracker — labels, colours, and which transitions notify the member."
                  actions={
                    <button onClick={startCreate} data-testid="ws-add-btn"
                            className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-4 py-2 rounded-md text-[13px]">
                      <Plus className="w-3.5 h-3.5" /> Add stage
                    </button>}>
      {loading ? (
        <p className="text-neutral-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <table className="w-full text-[13.5px]" data-testid="ws-table">
            <thead className="bg-neutral-50 text-[11.5px] uppercase tracking-wider text-neutral-600">
              <tr>
                <th className="text-left px-4 py-2.5 w-10"></th>
                <th className="text-left px-4 py-2.5 w-16">Order</th>
                <th className="text-left px-4 py-2.5">Stage</th>
                <th className="text-left px-4 py-2.5 w-44">Type</th>
                <th className="text-left px-4 py-2.5 w-28">Email</th>
                <th className="text-left px-4 py-2.5 w-24">Active</th>
                <th className="px-4 py-2.5 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {stages.map((s, i) => (
                <tr key={s.id} className="border-t border-neutral-100 align-top" data-testid={`ws-row-${s.code}`}>
                  <td className="px-2 py-3 text-center">
                    <div className="flex flex-col gap-0.5 text-neutral-400">
                      <button onClick={() => moveBy(i, -1)} disabled={i === 0} className="text-[10px] disabled:opacity-30" aria-label="Move up">▲</button>
                      <button onClick={() => moveBy(i, +1)} disabled={i === stages.length - 1} className="text-[10px] disabled:opacity-30" aria-label="Move down">▼</button>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-neutral-500 text-[12px]">{s.sort_order}</td>
                  <td className="px-4 py-3">
                    {editing === s.id ? (
                      <div className="space-y-2">
                        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                className="w-full px-2.5 py-1.5 rounded-md border border-neutral-300 text-[13px] focus:border-[#f5b300] outline-none" />
                        <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                placeholder="Description shown to the member"
                                className="w-full px-2.5 py-1.5 rounded-md border border-neutral-300 text-[12.5px] focus:border-[#f5b300] outline-none" />
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-neutral-500">Color:</span>
                          {COLORS.map((c) => (
                            <button key={c} aria-label={`Color ${c}`} type="button" onClick={() => setDraft({ ...draft, color_code: c })}
                                    className={`w-5 h-5 rounded-full border-2 ${draft.color_code === c ? 'border-[#14110f]' : 'border-white'}`}
                                    style={{ background: c }} />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="font-bold text-[#14110f] flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color_code }} aria-hidden="true" />
                          {s.name}
                        </p>
                        <p className="text-[12px] text-neutral-500 mt-0.5">{s.description}</p>
                        <p className="text-[11px] text-neutral-400 mt-1 font-mono">{s.code}</p>
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editing === s.id ? (
                      <div className="space-y-2">
                        <select value={draft.stage_type} onChange={(e) => setDraft({ ...draft, stage_type: e.target.value })}
                                className="w-full px-2.5 py-1.5 rounded-md border border-neutral-300 text-[13px] focus:border-[#f5b300] outline-none">
                          {STAGE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                        </select>
                        <label className="flex items-center gap-2 text-[12px] text-neutral-700">
                          <input type="checkbox" checked={!!draft.requires_client_action}
                                  onChange={(e) => setDraft({ ...draft, requires_client_action: e.target.checked })} />
                          Client action required
                        </label>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          s.stage_type === 'action_required' ? 'bg-amber-50 text-amber-800'
                            : s.stage_type === 'terminal' ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-neutral-100 text-neutral-700'}`}>{s.stage_type.replace('_', ' ')}</span>
                        {s.requires_client_action && (
                          <p className="text-[10.5px] text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Client action</p>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editing === s.id ? (
                      <label className="flex items-center gap-2 text-[12px] text-neutral-700">
                        <input type="checkbox" checked={!!draft.send_email}
                                onChange={(e) => setDraft({ ...draft, send_email: e.target.checked })} />
                        Send email
                      </label>
                    ) : s.send_email ? (
                      <span className="inline-flex items-center gap-1 text-[12px] text-emerald-700 font-semibold"><MailCheck className="w-3.5 h-3.5" /> Yes</span>
                    ) : <span className="text-[12px] text-neutral-400">No</span>}
                  </td>
                  <td className="px-4 py-3">
                    {editing === s.id ? (
                      <label className="flex items-center gap-2 text-[12px] text-neutral-700">
                        <input type="checkbox" checked={!!draft.active}
                                onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
                        Active
                      </label>
                    ) : s.active ? (
                      <span className="inline-flex items-center gap-1 text-[12px] text-emerald-700"><Eye className="w-3.5 h-3.5" /> Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[12px] text-neutral-400"><EyeOff className="w-3.5 h-3.5" /> Hidden</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editing === s.id ? (
                      <div className="flex items-center gap-1.5 justify-end">
                        <button onClick={save} data-testid="ws-save" className="inline-flex items-center gap-1 bg-[#14110f] hover:bg-[#291d12] text-white text-[12px] px-2.5 py-1.5 rounded-md font-semibold">
                          <Save className="w-3 h-3" /> Save
                        </button>
                        <button onClick={cancel} className="inline-flex items-center gap-1 text-[12px] text-neutral-500 hover:text-neutral-800 px-2 py-1.5">
                          <X className="w-3 h-3" /> Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 justify-end">
                        <button onClick={() => startEdit(s)} data-testid={`ws-edit-${s.code}`}
                                className="text-[12px] text-[#c89200] hover:underline font-semibold px-2 py-1">Edit</button>
                        {s.active && (
                          <button onClick={() => remove(s)} aria-label={`Deactivate ${s.name}`}
                                  className="text-neutral-400 hover:text-rose-600 p-1.5">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}

              {/* New-stage editor row */}
              {creating && draft && (
                <tr className="border-t border-neutral-100 bg-[#fff8e3]/50">
                  <td className="px-2 py-3 text-center text-neutral-400">
                    <GripVertical className="w-4 h-4 mx-auto" />
                  </td>
                  <td className="px-4 py-3 font-mono text-neutral-500 text-[12px]">new</td>
                  <td className="px-4 py-3 space-y-2">
                    <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                            placeholder="machine_code"
                            className="w-full px-2.5 py-1.5 rounded-md border border-neutral-300 text-[12.5px] font-mono focus:border-[#f5b300] outline-none" />
                    <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            placeholder="Stage name (shown to member)"
                            className="w-full px-2.5 py-1.5 rounded-md border border-neutral-300 text-[13px] focus:border-[#f5b300] outline-none" />
                    <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            placeholder="Description"
                            className="w-full px-2.5 py-1.5 rounded-md border border-neutral-300 text-[12.5px] focus:border-[#f5b300] outline-none" />
                  </td>
                  <td className="px-4 py-3">
                    <select value={draft.stage_type} onChange={(e) => setDraft({ ...draft, stage_type: e.target.value })}
                            className="w-full px-2.5 py-1.5 rounded-md border border-neutral-300 text-[13px] focus:border-[#f5b300] outline-none">
                      {STAGE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <label className="flex items-center gap-2 text-[12px]">
                      <input type="checkbox" checked={!!draft.send_email}
                              onChange={(e) => setDraft({ ...draft, send_email: e.target.checked })} />
                      Email
                    </label>
                  </td>
                  <td className="px-4 py-3">
                    <label className="flex items-center gap-2 text-[12px]">
                      <input type="checkbox" checked={!!draft.active}
                              onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
                      Active
                    </label>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button onClick={save} data-testid="ws-create-save" className="inline-flex items-center gap-1 bg-[#14110f] hover:bg-[#291d12] text-white text-[12px] px-2.5 py-1.5 rounded-md font-semibold">
                        <Save className="w-3 h-3" /> Create
                      </button>
                      <button onClick={cancel} className="inline-flex items-center gap-1 text-[12px] text-neutral-500 hover:text-neutral-800 px-2 py-1.5">
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
